import { VBMS_STATUS_LABELS } from '../components/SubmissionCard';
import {
  claimantName,
  isoDateOrBlank,
  longDateOrBlank,
  maskedIdentifier,
  statusLabel,
} from '../components/CombinedHistoryFields';
import { SUBMISSION_TYPES } from './combinedHistory';
import {
  COMBINED_HISTORY_FILES_FILTER,
  COMBINED_HISTORY_STATUS_FILTERS,
} from './constants';

/**
 * @file The search and the filters over the combined submission history,
 *   which keeps the whole merged list in the browser. Pure functions the
 *   history runs before it sorts and pages, so the table and the cards show
 *   the same rows.
 */

/**
 * @typedef {import('./combinedHistory').CombinedHistorySubmission} CombinedHistorySubmission
 */

/**
 * @typedef {Object} HistoryFilters
 * @property {string} query The search text, or an empty string.
 * @property {string|null} status One of `COMBINED_HISTORY_STATUS_FILTERS`,
 *   or null for all.
 * @property {string|null} form A submission type: a form number,
 *   `COMBINED_HISTORY_FILES_FILTER` for QuickSubmit uploads, or null for all.
 * @property {string|null} date A submitted date as `YYYY-MM-DD`, or null.
 * @property {string|null} confirmation Text the confirmation number must
 *   contain, or null. Kept in the history's state, never in the URL, as the
 *   search text is.
 */

/**
 * @typedef {Object} FilterOption
 * @property {string} value What the URL carries.
 * @property {string} label What the control shows.
 */

/**
 * The filters the modal holds, with nothing set.
 *
 * @type {Readonly<Pick<HistoryFilters, 'status'|'form'|'date'|'confirmation'>>}
 */
export const EMPTY_MODAL_FILTERS = Object.freeze({
  status: null,
  form: null,
  date: null,
  confirmation: null,
});

/**
 * The Status control's entries, each a VBMS status with the label the rows
 * show for it.
 *
 * @type {readonly FilterOption[]}
 */
export const STATUS_FILTERS = Object.freeze(
  COMBINED_HISTORY_STATUS_FILTERS.map(value => ({
    value,
    label: VBMS_STATUS_LABELS.get(value),
  })),
);

const FILES_LABEL = 'Files (QuickSubmit)';

/**
 * @param {string|null} status A `status` filter value.
 * @returns {string|null} The label the rows show for it, or null for a value
 *   the filter does not offer.
 */
const statusFilterLabel = status =>
  status && COMBINED_HISTORY_STATUS_FILTERS.includes(status)
    ? VBMS_STATUS_LABELS.get(status)
    : null;

/**
 * @param {string} form A submission type filter value.
 * @returns {string} What the control and the summary call it.
 */
const formLabel = form =>
  form === COMBINED_HISTORY_FILES_FILTER ? FILES_LABEL : form;

/**
 * @param {string|null|undefined} value
 * @returns {string} Lower-cased, for a case-insensitive match.
 */
const fold = value => (value ?? '').toString().toLowerCase();

/**
 * @param {string} query The search text as typed.
 * @returns {string[]} Its words, lower-cased: the text split on spaces and
 *   commas, so "Hilton, Mary" and "mary hilton" give the same words, and a
 *   text of only spaces and commas gives none.
 */
export const searchWords = query =>
  fold(query)
    .split(/[\s,]+/)
    .filter(Boolean);

/**
 * @param {CombinedHistorySubmission} submission
 * @param {string[]} words From `searchWords`, and not empty.
 * @returns {boolean} Whether every word appears in the claimant's name, the
 *   confirmation number, or the masked identifier (its last four). Each
 *   word may match a different field.
 */
const matchesQuery = (submission, words) => {
  const fields = [
    claimantName(submission),
    submission.confirmationNumber,
    maskedIdentifier(submission),
  ].map(fold);
  return words.every(word => fields.some(field => field.includes(word)));
};

/**
 * @param {CombinedHistorySubmission} submission
 * @param {string} form A form number or `COMBINED_HISTORY_FILES_FILTER`.
 * @returns {boolean}
 */
const matchesForm = (submission, form) =>
  form === COMBINED_HISTORY_FILES_FILTER
    ? submission.type === SUBMISSION_TYPES.QUICK_SUBMIT
    : submission.formType === form;

/**
 * @param {CombinedHistorySubmission} submission
 * @returns {string} The row's submitted date as `YYYY-MM-DD`, the day the
 *   table shows, or empty for a row without a readable date.
 */
export const submittedOn = submission =>
  isoDateOrBlank(submission.submittedDate);

/**
 * Keeps the rows that match the search and every filter together. Matching
 * is case-insensitive, and each word of the search matches anywhere in a
 * value, so "hilton", "mary", "Mary Hilton", and "Hilton, Mary" all find
 * "Hilton, Mary". An empty search and a null filter match every row.
 *
 * @param {CombinedHistorySubmission[]} submissions The loader's rows.
 * @param {HistoryFilters} filters
 * @returns {CombinedHistorySubmission[]} The matching rows in the given
 *   order, or `submissions` itself when nothing is filtered.
 * @example
 * filterSubmissions(rows, { ...EMPTY_MODAL_FILTERS, query: 'hilton', status: 'received' });
 */
export const filterSubmissions = (
  submissions,
  { query, status, form, date, confirmation },
) => {
  const words = searchWords(query);
  const wantedLabel = statusFilterLabel(status);
  const confirmationText = fold(confirmation).trim();
  if (
    words.length === 0 &&
    !wantedLabel &&
    !form &&
    !date &&
    !confirmationText
  ) {
    return submissions;
  }

  return submissions.filter(
    submission =>
      (words.length === 0 || matchesQuery(submission, words)) &&
      (!wantedLabel || statusLabel(submission) === wantedLabel) &&
      (!form || matchesForm(submission, form)) &&
      (!date || submittedOn(submission) === date) &&
      (!confirmationText ||
        fold(submission.confirmationNumber).includes(confirmationText)),
  );
};

/**
 * The Submission type control's entries for the loaded rows: each distinct
 * form number, in order, and a Files entry when any QuickSubmit upload is
 * loaded. Built from the loaded rows rather than the matching ones, so a
 * chosen entry stays in the list while it is chosen. A type in force that
 * no loaded row has, from an old link or after a reload dropped its rows,
 * is offered last, so the control shows the filter that is on and the
 * reader can change it.
 *
 * @param {CombinedHistorySubmission[]} submissions The loader's rows.
 * @param {string|null} [current] The submission type in force.
 * @returns {FilterOption[]}
 */
export const formFiltersFor = (submissions, current = null) => {
  const forms = Array.from(
    new Set(submissions.map(submission => submission.formType).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b, 'en-US', { numeric: true }));
  const options = forms.map(form => ({ value: form, label: form }));
  if (
    submissions.some(
      submission => submission.type === SUBMISSION_TYPES.QUICK_SUBMIT,
    )
  ) {
    options.push({
      value: COMBINED_HISTORY_FILES_FILTER,
      label: formLabel(COMBINED_HISTORY_FILES_FILTER),
    });
  }
  if (current && !options.some(option => option.value === current)) {
    options.push({ value: current, label: formLabel(current) });
  }
  return options;
};

/**
 * @param {HistoryFilters} filters
 * @returns {string[]} What the summary line says the rows match, one phrase
 *   per search or filter that is set, such as `matching “hilton”`. A search
 *   with no words, only spaces and commas, filters nothing and gets no
 *   phrase.
 */
export const filterPhrases = ({ query, status, form, date, confirmation }) => {
  const phrases = [];
  if (searchWords(query).length > 0) {
    phrases.push(`matching “${query.trim()}”`);
  }
  const shownStatus = statusFilterLabel(status);
  if (shownStatus) phrases.push(`with status “${shownStatus}”`);
  if (form) phrases.push(`of type “${formLabel(form)}”`);
  if (date) {
    const shown = longDateOrBlank(date);
    phrases.push(shown ? `submitted on ${shown}` : `submitted on ${date}`);
  }
  if (confirmation?.trim()) {
    phrases.push(
      `with confirmation number containing “${confirmation.trim()}”`,
    );
  }
  return phrases;
};
